import { FC, ReactNode } from 'react';
import styled from 'styled-components';
import { Variants, motion } from 'motion/react';
import { Caption, LabelBold, LabelRegular } from '../typography/typography';
import { ButtonOutlinedRounded } from '../buttons/buttons';

const variants: Variants = {
	enter: {
		height: 'auto',
		transition: {
			type: 'tween',
			duration: 0.5
		}
	},
	exit: {
		height: 0,
		width: '100%',
		overflowY: 'hidden',
		transition: {
			type: 'tween',
			duration: 0.5
		}
	}
};

type NotificationCardProps = {
	color?: string;
	captionFontColor?: string;
	labelFontColor?: string;
	buttonFontColor?: string;
	caption?: string;
	message: string;
	buttonText?: string;
	secondaryButtonText?: string;
	Icon: ReactNode;
	centerIcon?: boolean;
	buttonCallback?: () => void;
	secondaryButtonCallback?: () => void;
	secondaryBtn?: boolean;
	open: boolean;
	dataAt?: string;
	className?: string;
	onCloseClick?: () => void;
	animate?: boolean;
	children?: React.ReactNode;
};

type WrapperProps = {
	color: string;
	secondaryBtn?: boolean;
};

type IconWrapperProps = {
	centerIcon?: boolean;
};

type ButtonWrapperProps = {
	secondaryBtn?: boolean;
};

const Wrapper = motion.create(styled.div<WrapperProps>`
	width: 100%;
	border-radius: 3px;
	background-color: ${({ color }) => `rgba(var(${color}), 1)`};
`);

const Container = styled.div`
	display: grid;
	grid-template-columns: auto 1fr;
	grid-template-rows: repeat(2, 1fr) auto;
	grid-column-gap: 8px;
	grid-row-gap: 12px;
	padding: 12px;
`;

const IconWrapper = styled.div<IconWrapperProps>`
	display: flex;
	align-items: ${({ centerIcon }) => (centerIcon ? 'center' : 'unset')};
	grid-area: 1 / 1 / 4 / 2;
`;

const ButtonWrapper = styled.div<ButtonWrapperProps>`
	display: ${({ secondaryBtn }) => (secondaryBtn ? 'flex' : 'grid')};

	width: ${({ secondaryBtn }) => secondaryBtn && '100%'};
	grid-area: ${({ secondaryBtn }) => (secondaryBtn ? '4 / 4 / 3 / 1' : '3 / 2 / 4 / 3')};
	h5 {
		text-transform: var(--brand-text-transform);
	}
`;

const ContentWrapper = styled.div`
	grid-area: 1 / 2 / 3 / 3;
`;

const CaptionWrapper = styled(Caption)<WrapperProps>`
	color: ${({ color }) => `rgba(var(${color}), 1)`};
`;

const LabelWrapper = styled(LabelRegular)<WrapperProps>`
	color: ${({ color }) => `rgba(var(${color}), 1)`};
`;

const ButtonLabelWrapper = styled(LabelBold)<WrapperProps>`
	color: ${({ color }) => `rgba(var(${color}), 1)`};
`;

const Button = styled(ButtonOutlinedRounded)<WrapperProps>`
	border-color: ${({ color }) => `rgba(var(${color}), 1)`};
	height: auto;
	padding: 4px 6px;
	margin: ${({ secondaryBtn }) => secondaryBtn && '0 5px'};
`;

const NotificationCard: FC<NotificationCardProps> = ({
	color = '--error',
	caption,
	message,
	buttonText,
	secondaryButtonText,
	Icon,
	centerIcon,
	captionFontColor = '--font-light',
	labelFontColor = '--font-light',
	buttonFontColor = '--font-light',
	buttonCallback,
	secondaryButtonCallback,
	secondaryBtn,
	open,
	dataAt = '',
	className,
	onCloseClick,
	children,
	animate = true
}) => {
	return (
		<Wrapper
			color={color}
			variants={animate ? variants : undefined}
			animate={open ? 'enter' : 'exit'}
			initial="exit"
			exit="exit"
			className={className}
		>
			<Container data-at={dataAt}>
				<IconWrapper onClick={onCloseClick} centerIcon={centerIcon}>
					{Icon}
				</IconWrapper>
				<ContentWrapper>
					<CaptionWrapper color={captionFontColor}>{caption}</CaptionWrapper>
					<LabelWrapper my={1} color={labelFontColor} data-at={`${dataAt}-text`}>
						{message}
					</LabelWrapper>
				</ContentWrapper>
				{buttonText && (
					<ButtonWrapper secondaryBtn={secondaryBtn}>
						<Button
							color={buttonFontColor}
							onClick={buttonCallback}
							data-at={`${dataAt}-button`}
							secondaryBtn={secondaryBtn}
						>
							<ButtonLabelWrapper my={1} color={buttonFontColor}>
								{buttonText}
							</ButtonLabelWrapper>
						</Button>
						{secondaryButtonText && (
							<Button
								color={buttonFontColor}
								onClick={secondaryButtonCallback}
								data-at={`${dataAt}-secondary-button`}
								secondaryBtn={secondaryBtn}
							>
								<ButtonLabelWrapper my={1} color={buttonFontColor}>
									{secondaryButtonText}
								</ButtonLabelWrapper>
							</Button>
						)}
					</ButtonWrapper>
				)}
				{children}
			</Container>
		</Wrapper>
	);
};

export default NotificationCard;
